import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Camera, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiConfig';

/**
 * 照片上传组件
 * 支持拖拽上传、点击选择、实时预览、文件验证等功能
 */
const PhotoUploadField = ({
  label = "上传照片",
  value,
  onChange,
  required = false,
  disabled = false,
  maxSize = 240 * 1024, // DS-160要求：240KB
  acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png'],
  minDimensions = { width: 300, height: 300 },
  recommendedDimensions = { width: 600, height: 600 }
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(value?.url || value?.preview || '');
  const lastBlobUrlRef = useRef(null); // 记录最近一次用于预览的 blob URL，便于成功切换后释放
  const fileInputRef = useRef(null);

  // 文件验证函数
  const validateFile = useCallback((file) => {
    const errors = [];

    // 检查文件类型
    if (!acceptedFormats.includes(file.type)) {
      errors.push(`不支持的文件格式。请选择 ${acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} 格式的文件。`);
    }

    // 检查文件大小（DS-160要求：最大240KB）
    if (file.size > maxSize) {
      errors.push(`❌ 文件大小为 ${(file.size / 1024).toFixed(0)}KB，超过DS-160要求的240KB限制！请更换更小的照片或使用在线工具压缩（如 TinyPNG、Compressor.io）。建议尺寸：600×600像素。`);
    }

    return errors;
  }, [acceptedFormats, maxSize]);

  // 检查图片尺寸
  const validateImageDimensions = useCallback((file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const errors = [];
        
        if (img.width < minDimensions.width || img.height < minDimensions.height) {
          errors.push(`图片尺寸过小。最小要求 ${minDimensions.width}x${minDimensions.height} 像素。`);
        }

        resolve(errors);
      };
      img.onerror = () => {
        resolve(['无法读取图片文件，请确保文件未损坏。']);
      };
      img.src = URL.createObjectURL(file);
    });
  }, [minDimensions]);

  // 上传文件到服务器
  const uploadFileToServer = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/user/upload/photo', {
        method: 'POST',
        body: formData,
        credentials: 'include' // 包含认证cookies
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '上传失败');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('上传文件错误:', error);
      throw error;
    }
  }, []);

  // 预加载某个图片 URL，成功返回 true，失败返回 false
  const preloadImage = useCallback((url) => {
    return new Promise((resolve) => {
      if (!url) return resolve(false);
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }, []);

  // 将服务端返回的 url/file_path 规范化为可访问的绝对地址
  const normalizeServerUrl = useCallback((u) => {
    if (!u) return '';
    try {
      // 若 u 已是绝对 URL，new URL 会直接返回；否则基于后端 base URL 拼接
      return new URL(u, getApiBaseUrl()).href;
    } catch (e) {
      return '';
    }
  }, []);

  // 尝试将预览从 blob 切换到服务端 URL，失败则按指数回退重试几次
  const switchPreviewToServerUrl = useCallback(async (serverUrl, blobUrlToRevoke) => {
    if (!serverUrl) return; // 无可切换 URL，直接返回

    const delays = [400, 1000, 2000, 4000, 6000];
    for (let i = 0; i < delays.length; i++) {
      const ok = await preloadImage(serverUrl);
      if (ok) {
        setPreview(serverUrl);
        // 成功后释放之前的 blob URL（若有且仍为 blob）
        try {
          if (blobUrlToRevoke && blobUrlToRevoke.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrlToRevoke);
          }
        } catch (_) {}
        return;
      }
      await new Promise(r => setTimeout(r, delays[i]));
    }
    // 多次失败：保留 blob 预览，不做切换；稍后父组件若再次传入可用的 value.url，会由同步逻辑再次尝试
  }, [preloadImage]);

  // 处理文件选择
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    setError('');
    setIsUploading(true);

    try {
      // 基本文件验证
      const basicErrors = validateFile(file);
      if (basicErrors.length > 0) {
        setError(basicErrors.join(' '));
        setIsUploading(false);
        return;
      }

      // 图片尺寸验证
      const dimensionErrors = await validateImageDimensions(file);
      if (dimensionErrors.length > 0) {
        setError(dimensionErrors.join(' '));
        setIsUploading(false);
        return;
      }

      // 创建临时预览（不使用base64，使用对象URL）
      const previewUrl = URL.createObjectURL(file);
      lastBlobUrlRef.current = previewUrl;
      setPreview(previewUrl);

      // 上传文件到服务器
      const uploadResult = await uploadFileToServer(file);

      // 组装 photoData；规范化服务端 URL（可能是相对路径）
      const serverUrl = normalizeServerUrl(uploadResult?.url || uploadResult?.file_path);
      const photoData = {
        filename: uploadResult.filename,
        file_path: uploadResult.file_path,
        url: serverUrl || uploadResult.url, // 以规范化 URL 为主
        upload_status: 'completed',
        file_size: uploadResult.file_size,
        dimensions: uploadResult.dimensions,
        last_modified: uploadResult.last_modified
      };

      onChange(photoData);
      setIsUploading(false);

      // 尝试切换到服务端 URL；若暂时不可达，将维持 blob 预览并稍后重试
      if (serverUrl) {
        switchPreviewToServerUrl(serverUrl, previewUrl);
      }

    } catch (error) {
      console.error('文件处理错误:', error);
      setError(error.message || '处理文件时发生错误，请重试。');
      setIsUploading(false);
    }
  }, [validateFile, validateImageDimensions, uploadFileToServer, onChange, normalizeServerUrl, switchPreviewToServerUrl]);

  // 拖拽事件处理
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled, handleFileSelect]);

  // 点击选择文件
  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // 文件输入变化
  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // 删除照片
  const handleRemove = useCallback(() => {
    // 如果当前预览是对象URL，需要释放内存
    if (preview && preview.startsWith('blob:')) {
      try { URL.revokeObjectURL(preview); } catch (_) {}
    }

    setPreview('');
    setError('');
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange, preview]);

  // 当父组件传入的 value（尤其是 value.url）变更时，同步到内部预览；
  // 对于服务端 URL，先预加载通过再切换，避免 404 闪烁
  useEffect(() => {
    const next = (value && (value.url || value.preview)) || '';
    if (!next) return;

    // 同一个地址无需重复设置
    if (next === preview) return;

    // 服务端 URL 或相对路径：规范化后再预加载
    if (/^https?:/i.test(next) || next.startsWith('/')) {
      const abs = normalizeServerUrl(next);
      if (!abs || abs === preview) return;
      let cancelled = false;
      preloadImage(abs).then((ok) => {
        if (cancelled) return;
        if (ok) setPreview(abs);
        // 失败则保留原预览（可能是 blob），稍后还有机会由其它事件触发
      });
      return () => { cancelled = true; };
    }

    // 其它情况（data: 或 blob:），直接切换
    setPreview(next);
  }, [value, preview, normalizeServerUrl, preloadImage]);

  // 重新上传
  const handleRetry = useCallback(() => {
    setError('');
    handleClick();
  }, [handleClick]);

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="mb-6">
      {/* 标签 */}
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* 上传区域 */}
      {!preview ? (
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
            isDragOver
              ? 'border-blue-400 bg-blue-50'
              : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-sm text-gray-600">正在处理照片...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                {error ? (
                  <AlertCircle className="w-6 h-6 text-red-500" />
                ) : (
                  <Camera className="w-6 h-6 text-blue-600" />
                )}
              </div>
              
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  {error ? '上传失败' : '点击或拖拽上传照片'}
                </p>
                <p className="text-sm text-gray-600">
                  支持 JPG、PNG 格式，最大 240KB（DS-160要求）
                </p>
                <p className="text-xs text-gray-500">
                  建议尺寸: {recommendedDimensions.width}x{recommendedDimensions.height} 像素
                </p>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-100 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetry();
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    重新选择
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 隐藏的文件输入 */}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled}
          />
        </div>
      ) : (
        /* 预览区域 */
        <div className="space-y-4">
          <div className="relative inline-block">
            <img
              src={preview}
              alt="照片预览"
              className="w-48 h-48 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
            />
            
            {/* 删除按钮 */}
            <button
              onClick={handleRemove}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
              title="删除照片"
            >
              <X className="w-4 h-4" />
            </button>

            {/* 上传状态指示器 */}
            {value?.upload_status && (
              <div className="absolute bottom-2 right-2">
                {value.upload_status === 'completed' && (
                  <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                )}
                {value.upload_status === 'pending' && (
                  <div className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center">
                    <Upload className="w-4 h-4" />
                  </div>
                )}
                {value.upload_status === 'error' && (
                  <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                    <AlertCircle className="w-4 h-4" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 照片信息 */}
          {value && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">文件名:</span>
                  <span className="ml-2 text-gray-600">{value.filename}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">文件大小:</span>
                  <span className="ml-2 text-gray-600">{formatFileSize(value.file_size)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">图片尺寸:</span>
                  <span className="ml-2 text-gray-600">
                    {value.dimensions?.width}x{value.dimensions?.height} 像素
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">上传状态:</span>
                  <span className={`ml-2 ${
                    value.upload_status === 'completed' ? 'text-green-600' :
                    value.upload_status === 'pending' ? 'text-yellow-600' :
                    value.upload_status === 'error' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {value.upload_status === 'completed' ? '已上传' :
                     value.upload_status === 'pending' ? '待上传' :
                     value.upload_status === 'error' ? '上传失败' :
                     '未知状态'}
                  </span>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleClick}
                  className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>重新选择</span>
                </button>
                <button
                  onClick={handleRemove}
                  className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                  <span>删除照片</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoUploadField;
